import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { components as clientComponents } from 'mage-client';
import { MOET_CONSTANTS } from 'moet-core';
import { Message } from 'mage-i18n';
import {
  Strong,
  IconFile,
  Link,
  SpacerHorizontal,
  SpacerVertical,
  Typography,
  List,
} from '@klarna/bubble-ui';
import { CAPS } from '@/constants';
import { AttachmentType, AttachmentTypeEnum } from '@/constants/documents';
import { getWidgetTypeFromWidgetCookie } from '@/selectors/widget';
import {
  getMerchantAwareTranslator,
  getMerchantAwareTranslationKey,
  getMerchantAwareArrayTranslator,
  getUserMerchantId,
  merchantRegion,
} from '@/selectors/merchant';
import { tPrefix, ICON_MAP } from './constants';

import Uploader from './Uploader';
import Viewer from './Viewer';

import { logEvent } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from './analyticsEvents';

import { CY_DOCUMENTS_CONTAINER_IDS } from '@/containers/Documents/constants';
import { Container, Header, TitleAndDescription } from './styledComponents';

const { WIDGET_TYPE } = MOET_CONSTANTS;

type DocumentUploadBaseProps = {
  documents: StakeholderDocument[];
  metadata: { stakeholderId: string; stakeholderRoles: string[] };
  required?: boolean;
  type: AttachmentTypeEnum;
};

type ConnectProps = {
  isDocumentUpload: boolean;
  mid: string;
  region: string;
  t: TranslatorFunction;
  tArray: ArrayTranslatorFunction<{ link?: string; text: string }>;
  tKey: (key: string) => string;
};

type CapabilitiesProps = {
  deleteDocument: (args: {
    params: { mid: string; attachmentId: string };
    region: string;
  }) => Promise<void>;
  listDocumentsNonWidget: (args: { params: { mid: string }; region: string }) => Promise<void>;
  listDocumentsWidget: (args: { params: { mid: string }; region: string }) => Promise<void>;
  uploadDocument: (args: {
    body: FormData;
    onUploadProgress: (event) => void;
    params: {
      mid: string;
      stakeholderId?: string;
      type: string;
    };
    region: string;
  }) => Promise<void>;
};

type EnhancedDocumentUploadProps = DocumentUploadBaseProps & ConnectProps & CapabilitiesProps;

const DocumentUploadComponent = ({
  t,
  tArray,
  tKey,
  type = AttachmentType.SUPPORTING_DOCUMENT,
  documents,
  metadata,
  mid,
  region,
  required,
  isDocumentUpload,
  listDocumentsWidget,
  listDocumentsNonWidget,
  deleteDocument,
  uploadDocument,
}: EnhancedDocumentUploadProps) => {
  const [errors, setErrors] = useState<string[] | null>([]);

  const roles = metadata.stakeholderRoles?.length
    ? `_${metadata.stakeholderRoles?.toString()}`
    : '';

  const listDocuments = isDocumentUpload ? listDocumentsWidget : listDocumentsNonWidget;

  const Icon = ICON_MAP[type] || IconFile;

  const onDeleteDocument = useCallback(
    (doc: StakeholderDocument, callback: () => void) => {
      const removeDocument = async () => {
        await deleteDocument({ params: { mid, attachmentId: doc.attachmentId }, region });
        await listDocuments({ params: { mid }, region });

        logEvent(ANALYTICS_EVENTS.deleteDocument, { documentType: doc?.type });
        callback();
        setErrors(null);
      };
      if (documents?.length) {
        removeDocument();
      }
    },
    [documents, deleteDocument, listDocuments],
  );

  const onUploadDocument = useCallback(
    async (fileForm: FormData, { onUploadProgress }: { onUploadProgress: (event) => void }) => {
      await uploadDocument({
        params: {
          mid,
          type,
          ...(metadata.stakeholderId ? { stakeholderId: metadata.stakeholderId } : {}),
        },
        body: fileForm,
        region,
        onUploadProgress,
      });
      await listDocuments({ params: { mid }, region });
    },
    [metadata, uploadDocument, listDocuments],
  );

  const tPrefixExplanation = `${tPrefix}.explanation.${type}`;
  const explanationList = tArray(`${tPrefixExplanation}.list`);

  const strong = value => <Strong>{value}</Strong>;
  const link = (value: string) => {
    const href = t(`${tPrefixExplanation}.link`);
    return href ? (
      <Link href={href} target="_blank">
        {value}
      </Link>
    ) : (
      value
    );
  };

  return (
    <Container>
      <Header>
        {/* @ts-expect-error: FIXME */}
        <Icon style={{ minWidth: '20px' }} customSize={20} />
        <SpacerHorizontal spaceToken="space/300" />
        <TitleAndDescription>
          <Typography align="start" textToken="text-style/text/labels/body/bold">
            {t(`${tPrefixExplanation}.title`) || type}{' '}
            {!required && (
              <Typography align="start" textToken="text-style/text/labels/body/regular">
                {t(`${tPrefix}.optional`)}
              </Typography>
            )}
          </Typography>
          <SpacerHorizontal spaceToken="space/100" />
          <Typography
            align="start"
            colorToken="colors/text/neutral"
            textToken="text-style/text/paragraphs/default/regular"
          >
            <Message path={tKey(`${tPrefixExplanation}.subtitle`)} params={{ link, strong }} />
          </Typography>
          {explanationList && Array.isArray(explanationList) && (
            <List>
              {explanationList.map(r => (
                // @ts-expect-error: FIXME
                <List.Item key={r.text}>
                  <Typography
                    align="start"
                    colorToken="colors/text/neutral"
                    textToken="text-style/text/paragraphs/default/regular"
                  >
                    {r.text}
                  </Typography>
                </List.Item>
              ))}
            </List>
          )}
        </TitleAndDescription>
      </Header>
      <SpacerVertical spaceToken="space/150" />
      <Uploader
        type={type}
        onUploadDocument={onUploadDocument}
        cyElementId={CY_DOCUMENTS_CONTAINER_IDS.BUTTON_BROWSE_FILES + `_${type}` + `${roles}`}
        errors={errors}
        setErrors={setErrors}
      />
      {documents && (
        <Viewer documents={documents} onDeleteDocument={onDeleteDocument} data-testid="viewer" />
      )}
    </Container>
  );
};

export const DocumentUpload = compose<EnhancedDocumentUploadProps, DocumentUploadBaseProps>(
  connect<ConnectProps>(state => ({
    isDocumentUpload: getWidgetTypeFromWidgetCookie(state) === WIDGET_TYPE.DOCUMENT_UPLOAD,
    mid: getUserMerchantId(state),
    region: merchantRegion(state),
    t: getMerchantAwareTranslator(state),
    tArray: getMerchantAwareArrayTranslator(state),
    tKey: getMerchantAwareTranslationKey(state),
  })),
  // @ts-expect-error: FIXME
  clientComponents.withPromiseBasedCapabilities({
    deleteDocument: CAPS.V2_DOC_DELETE,
    listDocumentsNonWidget: CAPS.V2_DOC_LIST,
    listDocumentsWidget: CAPS.V2_DOC_TOKEN_INFO,
    uploadDocument: CAPS.V2_DOC_UPLOAD,
  } as CapabilitiesProps),
)(DocumentUploadComponent);
